import {
  POWER_UP_CATEGORIES,
  POWER_UP_PRICING_MODELS,
  type PowerUpCategory,
  type PowerUpDirectoryEntry,
  type PowerUpPricingModel,
} from '@molt/shared';
import { ExternalLink, PlugZap, Search } from 'lucide-react';
import type { FormEvent, ReactElement, ReactNode } from 'react';

import { Button, EmptyState, Notice, Skeleton, TextField } from '@/components';
import { cn } from '@/lib/cn';
import {
  useInterfaceLanguage,
  type InterfaceLanguageContextValue,
} from '@/lib/interface-language';

type InterfaceTranslator = InterfaceLanguageContextValue['t'];

export type PowerUpDirectoryCategoryFilter = PowerUpCategory | 'all';
export type PowerUpDirectoryPricingFilter = PowerUpPricingModel | 'all';

export interface PowerUpDirectoryProps {
  readonly entries: readonly PowerUpDirectoryEntry[];
  readonly query: string;
  readonly selectedCategory: PowerUpDirectoryCategoryFilter;
  readonly selectedPricingModel: PowerUpDirectoryPricingFilter;
  readonly madeByUsOnly: boolean;
  readonly loading?: boolean | undefined;
  readonly error?: string | undefined;
  readonly compact?: boolean | undefined;
  readonly boardContext?:
    { readonly boardId: string; readonly boardTitle: string } | null | undefined;
  readonly onQueryChange: (query: string) => void;
  readonly onSearch: (query: string) => void;
  readonly onSelectCategory: (category: PowerUpDirectoryCategoryFilter) => void;
  readonly onSelectPricingModel: (pricingModel: PowerUpDirectoryPricingFilter) => void;
  readonly onMadeByUsOnlyChange: (madeByUsOnly: boolean) => void;
  readonly onOpenDetail: (powerUp: PowerUpDirectoryEntry) => void;
  readonly renderEntryAction?:
    ((powerUp: PowerUpDirectoryEntry) => ReactNode) | undefined;
  readonly onRetry: () => void;
  readonly className?: string | undefined;
}

/**
 * The `PowerUpCategory` wire value → catalogue KEY table. A module-level
 * constant CANNOT call `t()`, so the constant holds the key and the render
 * resolves it — the `WorkspaceSwitcher` / `BOARD_ROLE_LABELS` shape.
 *
 * ⚠ The wire values are the RECORD KEYS and stay literal. They are what
 * `<option value=>`, the search params and `powerUpDirectoryQuerySchema` all
 * carry; routing one sends a translated string to the server and the schema
 * answers 422 — a 4xx that reads like an unrelated auth bug.
 */
export const POWER_UP_CATEGORY_KEYS: Record<PowerUpCategory, string> = {
  analytics: 'powerUps.category.analytics',
  automation: 'powerUps.category.automation',
  developer_tools: 'powerUps.category.developerTools',
  file_management: 'powerUps.category.fileManagement',
  communication: 'powerUps.category.communication',
  human_resources: 'powerUps.category.humanResources',
  it: 'powerUps.category.it',
  marketing: 'powerUps.category.marketing',
  product_design: 'powerUps.category.productDesign',
  project_management: 'powerUps.category.projectManagement',
  sales: 'powerUps.category.sales',
};

export const POWER_UP_PRICING_KEYS: Record<PowerUpPricingModel, string> = {
  free: 'powerUps.pricing.free',
  paid: 'powerUps.pricing.paid',
  requires_separate_subscription: 'powerUps.pricing.requiresSeparateSubscription',
};

/**
 * @deprecated Kept ONLY so `components/index.ts:238-239` still resolves — that
 * barrel is outside the FE-80.1 power-ups slice and renaming its re-exports
 * would fail typecheck in a file this slice may not edit. THESE HOLD CATALOGUE
 * KEYS, NOT ENGLISH.
 *
 * ⚠ The deciding axis was which dead shape fails LOUDLY. An alias holding keys
 * renders `powerUps.category.analytics` into the page if someone imports it
 * from the barrel and forgets `t` — visible garbage, caught on sight. Leaving
 * the stale English behind would render `Analytics` correctly forever in
 * English and never translate, which is the `boardMap.viewLabel` two-author
 * defect a routing slice exists to prevent. The pin asserts every value in both
 * aliases is a declared catalogue key, so an edit that puts English back is red.
 */
export const POWER_UP_CATEGORY_LABELS = POWER_UP_CATEGORY_KEYS;
/** @deprecated See `POWER_UP_CATEGORY_LABELS`. Holds catalogue KEYS. */
export const POWER_UP_PRICING_LABELS = POWER_UP_PRICING_KEYS;

function description(t: InterfaceTranslator, entry: PowerUpDirectoryEntry): string {
  const text = entry.description?.trim() ?? '';
  return text.length === 0 ? t('powerUps.noDescription') : text;
}

function publisher(t: InterfaceTranslator, entry: PowerUpDirectoryEntry): string {
  const text = entry.publisher?.trim() ?? '';
  return text.length === 0 ? t('powerUps.unknownPublisher') : text;
}

/**
 * ⚠ THE COMMA-JOIN STAYS A JOIN. `permissionGrants` is a variable-length list
 * of PROVIDER-authored labels; no single slotted key can express it, and
 * `Intl.ListFormat` is the real fix and a behaviour change. Only the empty
 * fallback is chrome. Reported as a residual, not taken here.
 */
function permissionText(t: InterfaceTranslator, entry: PowerUpDirectoryEntry): string {
  const disclosure = entry.permissionsDisclosure?.trim() ?? '';
  if (disclosure.length > 0) return disclosure;
  if (entry.permissionGrants.length === 0) return t('powerUps.noPermissions');
  return entry.permissionGrants.map((grant) => grant.label).join(', ');
}

export function PowerUpDirectory({
  entries,
  query,
  selectedCategory,
  selectedPricingModel,
  madeByUsOnly,
  loading = false,
  error,
  compact = false,
  boardContext,
  onQueryChange,
  onSearch,
  onSelectCategory,
  onSelectPricingModel,
  onMadeByUsOnlyChange,
  onOpenDetail,
  renderEntryAction,
  onRetry,
  className,
}: PowerUpDirectoryProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const trimmedQuery = query.trim();
  const gridClassName = compact
    ? 'grid grid-cols-1 gap-3'
    : 'grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-4';

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSearch(trimmedQuery);
  }

  return (
    <section
      aria-label={t('powerUps.directory.heading')}
      className={cn('flex flex-col gap-4 text-ink', className)}
    >
      <div className="flex flex-col gap-1">
        <h3 className="font-display text-lg font-semibold text-ink">
          {t('powerUps.directory.heading')}
        </h3>
        {boardContext === null || boardContext === undefined ? null : (
          <p className="text-sm text-ink-muted">
            {t('powerUps.directory.boardContext', { board: boardContext.boardTitle })}
          </p>
        )}
      </div>

      <form className="flex flex-col gap-3" noValidate onSubmit={handleSubmit}>
        <TextField
          type="search"
          label={t('powerUps.directory.searchLabel')}
          value={query}
          onChange={(event) => onQueryChange(event.currentTarget.value)}
          leadingIcon={<Search aria-hidden="true" className="size-4" />}
        />
        <div className={cn('grid gap-3', compact ? 'grid-cols-1' : 'md:grid-cols-3')}>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('powerUps.directory.categoryLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-sm text-ink"
              value={selectedCategory}
              onChange={(event) =>
                onSelectCategory(
                  event.currentTarget.value as PowerUpDirectoryCategoryFilter,
                )
              }
            >
              <option value="all">{t('powerUps.directory.allCategories')}</option>
              {POWER_UP_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {t(POWER_UP_CATEGORY_KEYS[category])}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-medium text-ink">
            {t('powerUps.directory.pricingLabel')}
            <select
              className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-sm text-ink"
              value={selectedPricingModel}
              onChange={(event) =>
                onSelectPricingModel(
                  event.currentTarget.value as PowerUpDirectoryPricingFilter,
                )
              }
            >
              <option value="all">{t('powerUps.directory.allPricing')}</option>
              {POWER_UP_PRICING_MODELS.map((pricingModel) => (
                <option key={pricingModel} value={pricingModel}>
                  {t(POWER_UP_PRICING_KEYS[pricingModel])}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2 self-end rounded-card border border-border bg-surface-raised px-3 py-2 text-sm font-medium text-ink">
            <input
              type="checkbox"
              checked={madeByUsOnly}
              onChange={(event) => onMadeByUsOnlyChange(event.currentTarget.checked)}
            />
            {t('powerUps.directory.madeByUsFilter')}
          </label>
        </div>
        <div>
          <Button
            type="submit"
            variant="primary"
            iconStart={<Search aria-hidden="true" className="size-4" />}
          >
            {t('powerUps.directory.search')}
          </Button>
        </div>
      </form>

      {error === undefined ? null : (
        <Notice
          tone="bad"
          title={t('powerUps.directory.loadFailedTitle')}
          body={error}
          action={
            <Button type="button" size="sm" variant="secondary" onClick={onRetry}>
              {t('powerUps.retry')}
            </Button>
          }
        />
      )}

      {loading ? (
        <div aria-busy="true" className={gridClassName}>
          {Array.from({ length: compact ? 3 : 6 }).map((_, index) => (
            <Skeleton key={index} shape="block" className="h-52" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          tone="neutral"
          headingLevel={compact ? 3 : 2}
          title={t('powerUps.directory.emptyTitle')}
          body={t('powerUps.directory.emptyBody')}
        />
      ) : (
        <div className={gridClassName}>
          {entries.map((entry) => {
            const name =
              entry.name.trim() === '' ? t('powerUps.untitledName') : entry.name;
            return (
              <article
                key={entry.id}
                aria-label={t('powerUps.directory.entryLabel', { powerUp: name })}
                className="flex min-h-52 flex-col gap-3 rounded-card border border-border bg-surface-raised p-3 shadow-card"
              >
                <div className="flex items-start gap-3">
                  {entry.iconUrl === null ? (
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-card bg-surface text-accent">
                      <PlugZap aria-hidden="true" className="size-5" />
                    </span>
                  ) : (
                    <img
                      src={entry.iconUrl}
                      alt=""
                      className="size-10 shrink-0 rounded-card bg-surface object-contain"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <h4 className="line-clamp-2 text-base font-semibold text-ink">
                      {name}
                    </h4>
                    <p className="text-xs text-ink-muted">
                      {t('powerUps.entryMeta', {
                        category: t(POWER_UP_CATEGORY_KEYS[entry.category]),
                        publisher: publisher(t, entry),
                      })}
                    </p>
                  </div>
                </div>

                <p className="line-clamp-3 text-sm text-ink-muted">
                  {description(t, entry)}
                </p>

                <dl className="flex flex-col gap-1 text-xs text-ink-muted">
                  <div>
                    <dt className="sr-only">{t('powerUps.pricingTerm')}</dt>
                    <dd>{t(POWER_UP_PRICING_KEYS[entry.pricingModel])}</dd>
                  </div>
                  <div>
                    <dt className="font-medium text-ink">
                      {t('powerUps.permissionsTerm')}
                    </dt>
                    <dd>{permissionText(t, entry)}</dd>
                  </div>
                </dl>

                <div className="mt-auto flex flex-wrap items-center gap-2">
                  {entry.madeByUs ? (
                    <span className="rounded-full border border-border bg-surface px-2 py-1 text-xs font-medium text-ink">
                      {t('powerUps.directory.madeByUsBadge')}
                    </span>
                  ) : null}
                  {entry.enabledOnBoard === true ? (
                    <span className="rounded-full border border-ok bg-surface px-2 py-1 text-xs font-medium text-ok">
                      {t('powerUps.enabledBadge')}
                    </span>
                  ) : null}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    iconStart={<ExternalLink aria-hidden="true" className="size-4" />}
                    onClick={() => onOpenDetail(entry)}
                  >
                    {t('powerUps.directory.viewDetails')}
                  </Button>
                  {renderEntryAction?.(entry)}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
